'use client';

/**
 * PassThroughStatement — per-org gross/fee/cost/net statement for money collected
 * (donations) and disbursed (card/incentive payouts), so a client CBO sees the
 * platform service fees and provider/processing costs passed through to it.
 *
 * Backed by GET /api/financial/client-statement (weavein auth, scoped by cbo_id).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface Statement {
  cbo_id: number;
  period: { from: string; to: string };
  collections: { count: number; gross_collected: number; platform_service_fee: number; net_to_org: number };
  disbursements: {
    count: number;
    gross_to_members: number;
    platform_service_fee: number;
    provider_cost: number;
    total_cost_to_org: number;
  };
  totals: { platform_service_fees: number; provider_costs: number };
}

const money = (n: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PassThroughStatement() {
  const [stmt, setStmt] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
      if (!cboId) { setError('No active organization'); setLoading(false); return; }
      const res = await authFetch(getWeaveinApiUrl(`/api/financial/client-statement?cbo_id=${cboId}`));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStmt(await res.json());
    } catch (e: any) {
      setError(e?.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading statement…
      </div>
    );
  }
  if (error || !stmt) {
    return null; // non-blocking: statement is supplementary to the reconciliation view
  }

  const c = stmt.collections;
  const d = stmt.disbursements;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-1 flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Pass-through statement</h3>
        <span className="text-xs text-gray-400">{stmt.period.from} → {stmt.period.to}</span>
      </div>
      <p className="mb-4 text-sm text-gray-500">
        Platform service fees and provider/processing costs on money collected and disbursed.
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Collections */}
        <div className="rounded-md border border-gray-100 p-4">
          <div className="mb-3 flex items-center gap-2 text-green-700">
            <ArrowDownCircle className="h-4 w-4" />
            <span className="font-medium">Collected (donations) · {c.count}</span>
          </div>
          <Row label="Gross collected" value={money(c.gross_collected)} />
          <Row label="Platform service fee" value={`- ${money(c.platform_service_fee)}`} muted />
          <Row label="Net to organization" value={money(c.net_to_org)} bold />
        </div>

        {/* Disbursements */}
        <div className="rounded-md border border-gray-100 p-4">
          <div className="mb-3 flex items-center gap-2 text-indigo-700">
            <ArrowUpCircle className="h-4 w-4" />
            <span className="font-medium">Disbursed (payouts) · {d.count}</span>
          </div>
          <Row label="Gross to members" value={money(d.gross_to_members)} />
          <Row label="Platform service fee" value={money(d.platform_service_fee)} muted />
          <Row label="Provider / processing cost" value={money(d.provider_cost)} muted />
          <Row label="Total cost to organization" value={money(d.total_cost_to_org)} bold />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-6 border-t border-gray-100 pt-3 text-sm">
        <div><span className="text-gray-500">Total platform service fees:</span> <span className="font-semibold">{money(stmt.totals.platform_service_fees)}</span></div>
        <div><span className="text-gray-500">Total provider costs:</span> <span className="font-semibold">{money(stmt.totals.provider_costs)}</span></div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, bold }: { label: string; value: string; muted?: boolean; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className={muted ? 'text-gray-400' : 'text-gray-600'}>{label}</span>
      <span className={bold ? 'font-semibold text-gray-900' : muted ? 'text-gray-400' : 'text-gray-800'}>{value}</span>
    </div>
  );
}
